/**
 * Ana haritada world 7'li geohash fotoğraflarını profil haritasındaki gibi
 * MarkerClusterGroup ile gösterir (birleşme/ayrılma). Explore isteği atılmaz.
 */
import React, { useMemo, useRef, useCallback, useState } from "react";
import { Marker } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-markercluster";
import L from "leaflet";
import Geohash from "latlon-geohash";
import siteConfig from "../siteConfig";
import GeohashService from "../services/geohash.service";
import DiscoverGallery from "./DiscoverGallery";
import "react-leaflet-markercluster/styles";
import "./UserPhotoMap.css";

/** Kare = gerçek daire. Boyut CSS (--fg-photo-marker-size) ile de kilitli. */
const MARKER_SIZE = 53;

const CF_RESIZE_PREFIX = "/cdn-cgi/image/";
/* Mobil ile aynı: yüksek çözünürlük + quality/sharpen — cansız thumbnail'i önler */
const THUMB_OPTS = `width=${MARKER_SIZE * 3},fit=cover,quality=95,sharpen=1,format=auto`;

function toThumbUrl(originalUrl) {
  if (!originalUrl) return originalUrl;
  if (!siteConfig.mapMarkerCloudflareResize) return originalUrl;
  try {
    const url = new URL(originalUrl);
    if (!url.origin.includes(new URL(siteConfig.cdnUrl).hostname))
      return originalUrl;
    return `${url.origin}${CF_RESIZE_PREFIX}${THUMB_OPTS}${url.pathname}`;
  } catch {
    return originalUrl;
  }
}

function createPhotoIcon(imageUrl) {
  const s = MARKER_SIZE;
  const escaped = (imageUrl || "")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;");
  return L.divIcon({
    className: "user-photo-map-marker-icon",
    html: `
      <div class="user-photo-map-marker-photo">
        ${escaped ? `<img src="${escaped}" alt="" loading="lazy" />` : '<div class="user-photo-map-marker-placeholder"></div>'}
      </div>
    `,
    iconSize: [s, s],
    iconAnchor: [s / 2, s / 2],
  });
}

function createClusterIconFactory(markerPhotoMap) {
  return function (cluster) {
    const count = cluster.getChildCount();
    const markers = cluster.getAllChildMarkers() || [];
    const clusterPhotos = markers
      .map((m) => {
        const ll = m.getLatLng();
        const key = `${Number(ll.lat).toFixed(5)}-${Number(ll.lng).toFixed(5)}`;
        return markerPhotoMap.get(key);
      })
      .filter(Boolean);
    const byPriority = clusterPhotos.slice().sort((a, b) => {
      const aRel = Boolean(a.is_religious);
      const bRel = Boolean(b.is_religious);
      if (aRel && !bRel) return -1;
      if (!aRel && bRel) return 1;
      return (b.score ?? 0) - (a.score ?? 0);
    });
    const top = byPriority[0];
    const url = top ? toThumbUrl(top.image_url) : null;
    const s = MARKER_SIZE;
    const stackOffset = 5;
    const wrapW = s + stackOffset * 2;
    const wrapH = s + stackOffset * 2;
    const anchorX = s / 2;
    const anchorY = s / 2;
    const escaped = (url || "").replace(/"/g, "&quot;").replace(/</g, "&lt;");
    const stackLayers =
      count > 1
        ? `
      <span class="user-photo-map-cluster-stack user-photo-map-cluster-stack-1"></span>
      <span class="user-photo-map-cluster-stack user-photo-map-cluster-stack-2"></span>
    `
        : "";
    return L.divIcon({
      className: "user-photo-map-cluster-icon",
      html: `
        <div class="user-photo-map-cluster-wrap" style="width:${wrapW}px;height:${wrapH}px;">
          ${stackLayers}
          <div class="user-photo-map-cluster-photo user-photo-map-cluster-photo-front">
            ${escaped ? `<img src="${escaped}" alt="" loading="lazy" />` : '<div class="user-photo-map-cluster-placeholder"></div>'}
          </div>
        </div>
      `,
      iconSize: [wrapW, wrapH],
      iconAnchor: [anchorX, anchorY],
    });
  };
}

function resolveCoords(d, geohashFallback) {
  const apiLat = typeof d?.lat === "number" ? d.lat : NaN;
  const apiLng = typeof d?.lng === "number" ? d.lng : NaN;
  if (
    Number.isFinite(apiLat) &&
    Number.isFinite(apiLng) &&
    !(apiLat === 0 && apiLng === 0)
  ) {
    return { lat: apiLat, lng: apiLng };
  }
  const hash =
    (typeof geohashFallback === "string" && geohashFallback) ||
    (typeof d?.geohash === "string" && d.geohash) ||
    (typeof d?.discover_geohash === "string" && d.discover_geohash);
  if (!hash) return null;
  try {
    const decoded = Geohash.decode(hash);
    return { lat: decoded.lat, lng: decoded.lon };
  } catch {
    return null;
  }
}

function mapWorldEntry(geohash, d) {
  const coords = resolveCoords(d, geohash);
  if (!coords) return null;
  return {
    geohash,
    geohash_id: d.geohash_id,
    image_url: d.url,
    score: d.score,
    user: d.user,
    user_links: d.user_links,
    is_commercial: d.is_commercial,
    is_religious: d.is_religious,
    lat: coords.lat,
    lng: coords.lng,
  };
}

function toGalleryPhotos(list) {
  return (Array.isArray(list) ? list : [])
    .map((d) => ({
      geohash_id: d.geohash_id,
      url: d.url || d.image_url,
      user: d.user,
      description: d.description,
    }))
    .filter((p) => p.url);
}

/**
 * worldGeohashData: { [geohash7]: { url, score, lat, lng, ... } }
 * Konum: API lat/lng (yoksa geohash merkezi). Tıklanınca discover paneli açılır.
 */
function WorldPhotoClusters({ worldGeohashData, isRouteMode }) {
  const [gallery, setGallery] = useState(null);
  const loadingCellRef = useRef(null);

  const photos = useMemo(() => {
    if (!worldGeohashData || typeof worldGeohashData !== "object") return [];
    const out = [];
    for (const [geohash, d] of Object.entries(worldGeohashData)) {
      if (!geohash || !d) continue;
      const photo = mapWorldEntry(geohash, d);
      if (photo) out.push(photo);
    }
    return out;
  }, [worldGeohashData]);

  const points = useMemo(
    () =>
      photos
        .filter(
          (p) =>
            typeof p.lat === "number" &&
            typeof p.lng === "number" &&
            Number.isFinite(p.lat) &&
            Number.isFinite(p.lng),
        )
        .map((photo) => ({ lat: photo.lat, lng: photo.lng, photo })),
    [photos],
  );

  const handlePhotoClick = useCallback(async (photo) => {
    const cell7 = (photo.geohash || "").slice(0, 7);
    if (!cell7 || loadingCellRef.current) return;

    loadingCellRef.current = cell7;
    try {
      const json = await GeohashService.getDiscoverData(cell7);
      const list = toGalleryPhotos(
        Array.isArray(json?.data) ? json.data : [],
      );
      const fallback =
        list.length > 0
          ? list
          : photo.image_url
            ? [
                {
                  geohash_id: photo.geohash_id,
                  url: photo.image_url,
                  user: photo.user,
                  description: photo.description,
                },
              ]
            : [];
      if (!fallback.length) return;
      const idx = fallback.findIndex(
        (p) => p.geohash_id && p.geohash_id === photo.geohash_id,
      );
      setGallery({ photos: fallback, focusIndex: idx >= 0 ? idx : 0 });
    } catch (err) {
      console.error("Discover yüklenemedi:", err);
    } finally {
      loadingCellRef.current = null;
    }
  }, []);

  const markerPhotoMap = useMemo(() => {
    const map = new Map();
    points.forEach(({ lat, lng, photo }) => {
      const key = `${Number(lat).toFixed(5)}-${Number(lng).toFixed(5)}`;
      map.set(key, {
        image_url: photo.image_url || null,
        score: photo.score != null ? Number(photo.score) : 0,
        is_religious: Boolean(photo.is_religious),
      });
    });
    return map;
  }, [points]);

  const markerPhotoMapRef = useRef(markerPhotoMap);
  markerPhotoMapRef.current = markerPhotoMap;

  const iconCreateFunction = useCallback(
    (cluster) => createClusterIconFactory(markerPhotoMapRef.current)(cluster),
    [],
  );

  const iconByUrl = useMemo(() => {
    const map = new Map();
    map.set("__placeholder", createPhotoIcon(null));
    points.forEach(({ photo }) => {
      const url = photo.image_url;
      if (url && !map.has(url)) map.set(url, createPhotoIcon(toThumbUrl(url)));
    });
    return map;
  }, [points]);

  if (isRouteMode || !photos.length) return null;

  return (
    <>
      <MarkerClusterGroup
        key={`photo-clusters-${MARKER_SIZE}`}
        maxClusterRadius={45}
        disableClusteringAtZoom={14}
        spiderfyOnMaxZoom={true}
        showCoverageOnHover={false}
        zoomToBoundsOnClick={true}
        iconCreateFunction={iconCreateFunction}
      >
        {points.map(({ lat, lng, photo }, i) => (
          <Marker
            key={photo.geohash_id || `${lat}-${lng}-${i}`}
            position={[lat, lng]}
            icon={
              iconByUrl.get(photo.image_url) ?? iconByUrl.get("__placeholder")
            }
            eventHandlers={{
              click: () => handlePhotoClick(photo),
            }}
          />
        ))}
      </MarkerClusterGroup>
      {gallery ? (
        <DiscoverGallery
          photos={gallery.photos}
          focusIndex={gallery.focusIndex}
          onClose={() => setGallery(null)}
        />
      ) : null}
    </>
  );
}

// Harita üzerinde konum seçerken App state'i sık güncellendiği için
// gereksiz cluster yeniden-mount'larını engellemek adına memoize ediyoruz.
export default React.memo(WorldPhotoClusters);
